import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import type { ReactNode } from "react";
import { authApi } from "../api/auth";
import type { User, LoginCredentials } from "../api/auth";
import { TokenRefreshManager } from "../utils/tokenRefresh";
import { tokenStorage } from "../utils/tokenStorage";

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const tokenRefreshManager = useRef<TokenRefreshManager | null>(null);

  useEffect(() => {
    // Check if user is already logged in
    const initAuth = async () => {
      const token = tokenStorage.getAccessToken();
      const storedUser = tokenStorage.getUser();

      if (token && storedUser) {
        try {
          // Always fetch fresh user data from server on page load
          // This ensures we have the latest permissions and isSuperAdmin flag
          const currentUser = await authApi.getCurrentUser();
          setUser(currentUser);
          // Update storage with fresh data
          tokenStorage.setUser(currentUser);

          // Start token refresh manager
          startTokenRefreshManager();
        } catch (error) {
          console.error("Auth initialization error:", error);
          
          // Only clear tokens on authentication errors (401/403)
          const errorStatus = (error as any)?.response?.status;
          const isAuthError = errorStatus === 401 || errorStatus === 403;
          
          if (isAuthError) {
            console.warn(`Authentication failed with status ${errorStatus}, clearing cached credentials`);
            tokenStorage.clearAll();
            setUser(null);
          } else {
            // For non-auth errors (network issues, server down), try to use cached data
            // but warn that it might be stale
            console.warn(`Non-auth error during initialization (status ${errorStatus}), using cached user data (may be stale)`);
            console.warn('Consider refreshing once server is available to get fresh user data');
            setUser(storedUser);
            
            // Try to start token refresh manager even with cached data
            try {
              startTokenRefreshManager();
            } catch (refreshError) {
              console.warn('Could not start token refresh manager with cached data:', refreshError);
            }
          }
        }
      }
      setIsLoading(false);
    };

    initAuth();

    // Cleanup on unmount
    return () => {
      stopTokenRefreshManager();
    };
  }, []);

  const startTokenRefreshManager = () => {
    if (!tokenRefreshManager.current) {
      tokenRefreshManager.current = new TokenRefreshManager({
        checkIntervalMs: 5 * 60 * 1000, // Check every 5 minutes
        refreshThresholdMinutes: 5, // Refresh when < 5 minutes remaining (safety buffer)
        activityTimeoutMinutes: 60, // Consider inactive after 60 minutes of no activity
        onTokenRefreshed: async (token) => {
          console.log("✅ Token refreshed, updating user data");
          try {
            const currentUser = await authApi.getCurrentUser();
            setUser(currentUser);
            tokenStorage.setUser(currentUser);
          } catch (error) {
            console.error("Failed to update user after token refresh:", error);
          }
        },
        onRefreshFailed: () => {
          console.error("❌ Token refresh failed, logging out");
          logout();
        },
      });
      tokenRefreshManager.current.start();
    }
  };

  const stopTokenRefreshManager = () => {
    if (tokenRefreshManager.current) {
      tokenRefreshManager.current.stop();
      tokenRefreshManager.current = null;
    }
  };

  const login = async (credentials: LoginCredentials) => {
    try {
      const response = await authApi.login(credentials);

      // Check if MFA is required
      if (response.mfaRequired) {
        // Return response to caller so they can handle MFA
        return response;
      }

      // Normal login flow
      const { token, refreshToken, user: userData } = response;

      tokenStorage.setAccessToken(token);
      if (refreshToken) {
        tokenStorage.setRefreshToken(refreshToken);
      }
      tokenStorage.setUser(userData);
      
      console.log("🔐 [AuthContext] Login successful, user data:", {
        email: userData.email,
        isSuperAdmin: userData.isSuperAdmin,
        tenantId: userData.tenantId,
        permissionCodes: userData.permissionCodes,
        permissionCount: userData.permissionCodes?.length || 0,
      });
      
      setUser(userData);

      // Start token refresh manager after successful login
      startTokenRefreshManager();

      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Stop token refresh manager
      stopTokenRefreshManager();

      await authApi.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      tokenStorage.clearAll();
      setUser(null);
    }
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
